import { inject } from '@vercel/analytics';
inject();

import * as THREE from 'three';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
const canvas = document.getElementById('webgl-canvas');
const scene = new THREE.Scene();

const camera = new THREE.PerspectiveCamera(
  75,
  window.innerWidth / window.innerHeight,
  0.1,
  1000
);
camera.position.set(0, 30, 50);
camera.lookAt(0, 0, 0);

const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  alpha: true,
});
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setClearColor(0x050505, 1);

const gridSize = 80;
const segments = 80;
const waveGeometry = new THREE.PlaneGeometry(gridSize, gridSize, segments, segments);

const waveMaterial = new THREE.ShaderMaterial({
  uniforms: {
    uTime: { value: 0 },
    uMouse: { value: new THREE.Vector2(0.5, 0.5) },
    uColorA: { value: new THREE.Color(0xfbbf24) },
    uColorB: { value: new THREE.Color(0x3b82f6) },
  },
  vertexShader: `
    uniform float uTime;
    uniform vec2 uMouse;

    varying vec2 vUv;
    varying float vElevation;

    void main() {
      vUv = uv;

      vec3 pos = position;

      // Multiple wave layers
      float wave1 = sin(pos.x * 0.15 + uTime * 0.5) * 2.0;
      float wave2 = sin(pos.y * 0.1 + uTime * 0.3) * 1.5;
      float wave3 = sin((pos.x + pos.y) * 0.1 + uTime * 0.4) * 1.0;

      // Mouse influence
      float distToMouse = distance(uv, uMouse);
      float mouseWave = sin(distToMouse * 10.0 - uTime * 2.0) * (1.0 - smoothstep(0.0, 0.5, distToMouse)) * 3.0;

      pos.z = wave1 + wave2 + wave3 + mouseWave;
      vElevation = pos.z;

      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    }
  `,
  fragmentShader: `
    uniform vec3 uColorA;
    uniform vec3 uColorB;
    uniform float uTime;

    varying vec2 vUv;
    varying float vElevation;

    void main() {
      // Color based on elevation
      float mixStrength = (vElevation + 5.0) / 10.0;
      vec3 color = mix(uColorB, uColorA, mixStrength);

      // Add some glow based on elevation
      float glow = smoothstep(-2.0, 5.0, vElevation) * 0.3;
      color += glow;

      // Fade edges
      float edgeFade = smoothstep(0.0, 0.1, vUv.x) * smoothstep(1.0, 0.9, vUv.x);
      edgeFade *= smoothstep(0.0, 0.1, vUv.y) * smoothstep(1.0, 0.9, vUv.y);

      gl_FragColor = vec4(color, edgeFade * 0.15);
    }
  `,
  transparent: true,
  side: THREE.DoubleSide,
  wireframe: true,
});

const waveMesh = new THREE.Mesh(waveGeometry, waveMaterial);
waveMesh.rotation.x = -Math.PI / 2.5;
waveMesh.position.y = -15;
scene.add(waveMesh);

const orbs = [];
const orbCount = 5;

for (let i = 0; i < orbCount; i++) {
  const geometry = new THREE.SphereGeometry(0.5 + Math.random() * 1, 32, 32);
  const material = new THREE.MeshBasicMaterial({
    color: i % 2 === 0 ? 0xfbbf24 : 0x3b82f6,
    transparent: true,
    opacity: 0.1 + Math.random() * 0.1,
  });

  const orb = new THREE.Mesh(geometry, material);

  orb.position.x = (Math.random() - 0.5) * 60;
  orb.position.y = Math.random() * 20 - 5;
  orb.position.z = (Math.random() - 0.5) * 40;

  orb.userData = {
    originalY: orb.position.y,
    speed: 0.3 + Math.random() * 0.5,
    amplitude: 3 + Math.random() * 5,
    phase: Math.random() * Math.PI * 2,
  };

  orbs.push(orb);
  scene.add(orb);
}

const particleCount = 500;
const particleGeometry = new THREE.BufferGeometry();
const particlePositions = new Float32Array(particleCount * 3);

for (let i = 0; i < particleCount; i++) {
  particlePositions[i * 3] = (Math.random() - 0.5) * 100;
  particlePositions[i * 3 + 1] = Math.random() * 60 - 20;
  particlePositions[i * 3 + 2] = (Math.random() - 0.5) * 100;
}

particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));

const particleMaterial = new THREE.PointsMaterial({
  size: 0.1,
  color: 0xffffff,
  transparent: true,
  opacity: 0.3,
  blending: THREE.AdditiveBlending,
});

const particles = new THREE.Points(particleGeometry, particleMaterial);
scene.add(particles);

const accents = [];

for (let i = 0; i < 3; i++) {
  const ringGeo = new THREE.TorusGeometry(3 + i * 2, 0.05, 16, 100);
  const ringMat = new THREE.MeshBasicMaterial({
    color: i === 1 ? 0x3b82f6 : 0xfbbf24,
    transparent: true,
    opacity: 0.1,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);

  ring.position.set(
    30 + i * 5,
    10 - i * 5,
    -20 - i * 10
  );
  ring.rotation.x = Math.PI / 4 + i * 0.2;
  ring.rotation.y = i * 0.5;

  ring.userData = {
    rotationSpeed: 0.002 + i * 0.001,
  };

  accents.push(ring);
  scene.add(ring);
}

const mouse = { x: 0.5, y: 0.5 };
const targetMouse = { x: 0.5, y: 0.5 };

document.addEventListener('mousemove', (event) => {
  targetMouse.x = event.clientX / window.innerWidth;
  targetMouse.y = 1.0 - (event.clientY / window.innerHeight);
});

function handleResize() {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
}

window.addEventListener('resize', handleResize);

const clock = new THREE.Clock();

function animate() {
  requestAnimationFrame(animate);

  const elapsedTime = clock.getElapsedTime();

  mouse.x += (targetMouse.x - mouse.x) * 0.05;
  mouse.y += (targetMouse.y - mouse.y) * 0.05;

  waveMaterial.uniforms.uTime.value = elapsedTime;
  waveMaterial.uniforms.uMouse.value.set(mouse.x, mouse.y);
  orbs.forEach((orb) => {
    orb.position.y = orb.userData.originalY +
      Math.sin(elapsedTime * orb.userData.speed + orb.userData.phase) * orb.userData.amplitude;
    orb.rotation.y = elapsedTime * 0.2;
  });
  particles.rotation.y = elapsedTime * 0.02;

  accents.forEach((ring) => {
    ring.rotation.z += ring.userData.rotationSpeed;
    ring.rotation.x += ring.userData.rotationSpeed * 0.5;
  });
  camera.position.x = Math.sin(elapsedTime * 0.1) * 2;
  camera.position.y = 30 + Math.sin(elapsedTime * 0.15) * 2;

  renderer.render(scene, camera);
}
function initIntroAnimation() {
  const tl = gsap.timeline({
    defaults: { ease: 'power3.out' },
  });

  gsap.fromTo(camera.position,
    { z: 100, y: 50 },
    { z: 50, y: 30, duration: 2, ease: 'power2.out' }
  );
  tl.to('#loader', {
    opacity: 0,
    duration: 0.5,
    onComplete: () => {
      const loader = document.getElementById('loader');
      if (loader) loader.style.display = 'none';
    },
  })
    .to('#content', {
      opacity: 1,
      duration: 0.3,
    })
    .fromTo('.hero-subtitle',
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6 },
      '-=0.1'
    )
    .fromTo('.hero-title',
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.8 },
      '-=0.4'
    )
    .fromTo('.hero-role',
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6 },
      '-=0.5'
    )
    .fromTo('.hero-buttons',
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6 },
      '-=0.4'
    )
    .fromTo('.hero-socials',
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6 },
      '-=0.4'
    )
    .fromTo('.scroll-indicator',
      { opacity: 0 },
      { opacity: 1, duration: 0.6 },
      '-=0.3'
    );
}

function initScrollAnimations() {
  gsap.fromTo('#about .section-header',
    { opacity: 0, y: 30 },
    {
      opacity: 1, y: 0, duration: 0.8,
      scrollTrigger: {
        trigger: '#about',
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      }
    }
  );

  gsap.fromTo('#about .stats-grid',
    { opacity: 0, y: 30 },
    {
      opacity: 1, y: 0, duration: 0.8, delay: 0.2,
      scrollTrigger: {
        trigger: '#about',
        start: 'top 70%',
        toggleActions: 'play none none reverse',
      }
    }
  );

  gsap.fromTo('#about .about-bio',
    { opacity: 0, x: 30 },
    {
      opacity: 1, x: 0, duration: 0.8, delay: 0.3,
      scrollTrigger: {
        trigger: '#about',
        start: 'top 60%',
        toggleActions: 'play none none reverse',
      }
    }
  );

  gsap.fromTo('#skills .section-header',
    { opacity: 0, y: 30 },
    {
      opacity: 1, y: 0, duration: 0.8,
      scrollTrigger: {
        trigger: '#skills',
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      }
    }
  );

  gsap.fromTo('#skills .skills-card',
    { opacity: 0, y: 40 },
    {
      opacity: 1, y: 0, duration: 0.8, stagger: 0.2,
      scrollTrigger: {
        trigger: '#skills',
        start: 'top 60%',
        toggleActions: 'play none none reverse',
      }
    }
  );

  gsap.fromTo('#skills .skill-item',
    { opacity: 0, x: -20 },
    {
      opacity: 1, x: 0, duration: 0.4, stagger: 0.05,
      scrollTrigger: {
        trigger: '#skills .skills-list',
        start: 'top 70%',
        toggleActions: 'play none none reverse',
      }
    }
  );
  gsap.fromTo('#contact .section-header',
    { opacity: 0, y: 30 },
    {
      opacity: 1, y: 0, duration: 0.8,
      scrollTrigger: {
        trigger: '#contact',
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      }
    }
  );

  gsap.fromTo('#contact .contact-card',
    { opacity: 0, y: 20 },
    {
      opacity: 1, y: 0, duration: 0.6, stagger: 0.15,
      scrollTrigger: {
        trigger: '#contact',
        start: 'top 70%',
        toggleActions: 'play none none reverse',
      }
    }
  );
  ScrollTrigger.create({
    trigger: 'body',
    start: 'top top',
    end: 'bottom bottom',
    scrub: 1,
    onUpdate: (self) => {
      const progress = self.progress;
      waveMesh.rotation.z = progress * 0.3;
      camera.position.y = 30 - progress * 20;
      waveMesh.position.z = progress * -20;
    },
  });
}
function initMobileMenu() {
  const menuToggle = document.getElementById('menu-toggle');
  const mobileMenu = document.getElementById('mobile-menu');
  const mobileLinks = document.querySelectorAll('.mobile-nav-link');

  if (!menuToggle || !mobileMenu) return;

  menuToggle.addEventListener('click', () => {
    menuToggle.classList.toggle('active');
    mobileMenu.classList.toggle('open');
  });

  mobileLinks.forEach((link) => {
    link.addEventListener('click', () => {
      menuToggle.classList.remove('active');
      mobileMenu.classList.remove('open');
    });
  });
}

function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    });
  });
}
function init() {
  animate();

  window.addEventListener('load', () => {
    initIntroAnimation();
    initScrollAnimations();
    initMobileMenu();
    initSmoothScroll();
  });
}

init();
